import { skipToken, useQueries, useQuery } from '@tanstack/react-query';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, ChatLineStatus, type ChatLineStatusKind } from '@gitbook/spine-react';

import { apiQuery, useCurrentOrganizationId } from '../../state';
import {
    cancelImportRunWithToast,
    countImportedPages,
    isImportRunStatusActive,
    useImportRun,
} from '../Imports';
import { getOpenAPIProcessingErrorMessage } from '../OpenAPI';

type ImportRunStatus = api.ContentImportRun['status'];

/** Share of the progress bar, in percent, shown as soon as the import starts finding pages. */
const FINDING_PAGES_SHARE = 10;

/** Share of the progress bar, in percent, filled as pages are imported; the rest is finishing up. */
const IMPORTING_PAGES_SHARE = 80;

/** Number of imported pages at which the bar is halfway through the importing step. */
const HALFWAY_PAGES = 5;

/** How often a spec being processed is read again, in milliseconds. */
const OPENAPI_SPEC_POLL_INTERVAL = 3000;

const STATUS: Record<ImportRunStatus, { label: string; kind: ChatLineStatusKind }> = {
    pending: { label: 'Working', kind: 'working' },
    'in-progress': { label: 'Working', kind: 'working' },
    completed: { label: 'Done', kind: 'success' },
    failed: { label: 'Failed', kind: 'danger' },
    cancelled: { label: 'Cancelled', kind: 'neutral' },
};

/**
 * An import the agent started, with its live progress and the action that fits where it stands:
 * cancel it while it runs, review the change request once it is done.
 */
export function ImportRunCard(props: {
    toolCall: Extract<api.AIToolCall, { tool: 'startContentImport' }>;
    /** What the content is imported from, as the transcript names it. */
    source: string;
    /** Shown instead when the run cannot be read, so its status is never guessed. */
    fallback: React.ReactNode;
}) {
    const { toolCall, source, fallback } = props;
    const organizationId = useCurrentOrganizationId();
    const run = useImportRun({ organizationId, runId: toolCall.runId });

    if (run === null) {
        return fallback;
    }

    // Until the server answers, the import is what the agent just started.
    const status = run?.status ?? 'pending';
    const { imported, total } = run ? countImportedPages(run) : { imported: 0, total: 0 };
    const running = isImportRunStatusActive(status);

    return (
        <ImportCard
            icon={toolCall.url ? Icon.Globe : Icon.Download}
            title={
                <>
                    {status === 'completed' ? 'Imported' : running ? 'Importing' : 'Import'} from{' '}
                    <strong>{source}</strong>
                </>
            }
            status={STATUS[status]}
            running={running}
            progress={<ImportProgressBar imported={imported} />}
            detail={getDetail({
                status,
                imported,
                total,
                reason: run?.reason,
                website: Boolean(toolCall.url),
            })}
            failed={status === 'failed'}
            action={
                running ? (
                    organizationId ? (
                        <Button
                            size="xsmall"
                            kind="blank"
                            label="Cancel"
                            onPress={() => cancelImportRunWithToast(organizationId, toolCall.runId)}
                        />
                    ) : null
                ) : (
                    <ReviewChangesButton
                        organizationId={organizationId}
                        toolCall={toolCall}
                        completed={status === 'completed'}
                    />
                )
            }
        />
    );
}

/**
 * An OpenAPI spec the agent added, followed until GitBook has processed it and its API reference
 * is generated.
 */
export function OpenAPIImportCard(props: {
    toolCall: Extract<api.AIToolCall, { tool: 'importOpenAPISpec' }>;
}) {
    const { toolCall } = props;
    const organizationId = useCurrentOrganizationId();
    const spec = useAgentOpenAPISpec({ organizationId, specSlug: toolCall.specSlug });

    const status: ImportRunStatus = isOpenAPISpecImporting(spec)
        ? 'in-progress'
        : spec === null || spec?.lastProcessErrorCode
          ? 'failed'
          : 'completed';
    const running = isImportRunStatusActive(status);

    return (
        <ImportCard
            icon={toolCall.url ? Icon.Globe : Icon.Download}
            title={
                <>
                    {status === 'completed' ? 'Imported' : running ? 'Importing' : 'Import'} the API
                    reference from{' '}
                    <strong>{toolCall.url ?? toolCall.file ?? toolCall.specSlug}</strong>
                </>
            }
            status={STATUS[status]}
            running={running}
            detail={
                spec?.lastProcessErrorCode
                    ? getOpenAPIProcessingErrorMessage(spec.lastProcessErrorCode)
                    : spec === null
                      ? 'The spec could not be found.'
                      : status === 'completed'
                        ? 'API reference generated'
                        : 'Generating the API reference…'
            }
            failed={status === 'failed'}
            action={
                running ? null : (
                    <ReviewChangesButton
                        organizationId={organizationId}
                        toolCall={toolCall}
                        completed={status === 'completed'}
                    />
                )
            }
        />
    );
}

/**
 * An OpenAPI spec the agent added, read again until GitBook has processed it. `null` when it no
 * longer exists, `undefined` until the server answers.
 */
export function useAgentOpenAPISpec(props: {
    organizationId: string | null;
    specSlug: string | null;
}): api.OpenAPISpec | null | undefined {
    const { organizationId, specSlug } = props;
    const { data } = useQuery(getAgentOpenAPISpecQuery(organizationId, specSlug));
    return data;
}

/**
 * Whether GitBook is still processing any of these specs the agent added.
 */
export function useAnyOpenAPISpecImporting(props: {
    organizationId: string | null;
    specSlugs: string[];
}): boolean {
    const { organizationId, specSlugs } = props;
    return useQueries({
        queries: specSlugs.map((specSlug) => getAgentOpenAPISpecQuery(organizationId, specSlug)),
        combine: (results) => results.some((result) => isOpenAPISpecImporting(result.data)),
    });
}

/**
 * Whether GitBook is still processing a spec the agent added. One the server has not reported
 * yet was only just added.
 */
export function isOpenAPISpecImporting(spec: api.OpenAPISpec | null | undefined): boolean {
    return (
        spec === undefined ||
        (spec !== null && spec.processingState !== api.OpenAPISpecProcessingState.Complete)
    );
}

/**
 * The frame every import card shares: what is imported and its status, then a line of detail
 * with the action that fits.
 */
function ImportCard(props: {
    icon: React.ComponentType<{ className?: string }>;
    title: React.ReactNode;
    status: { label: string; kind: ChatLineStatusKind };
    running: boolean;
    /** Shown while the import runs. */
    progress?: React.ReactNode;
    detail: string;
    failed: boolean;
    action: React.ReactNode;
}) {
    const { icon: SourceIcon, title, status, running, progress, detail, failed, action } = props;

    return (
        <div className="my-1 flex flex-col gap-2.5 rounded-lg border border-neutral-subtle bg-base p-3">
            <div className="flex items-start gap-2 text-ui-small">
                <span className="flex size-4 shrink-0 items-center justify-center text-muted">
                    <SourceIcon className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1 font-medium break-words text-neutral-high">
                    {title}
                </span>
                <ChatLineStatus
                    kind={status.kind}
                    dotClassName={running ? 'bg-primary-solid' : undefined}
                >
                    {status.label}
                </ChatLineStatus>
            </div>

            {running && progress ? <div className="pl-6">{progress}</div> : null}

            <div className="flex items-center gap-2 pl-6">
                <span
                    className={
                        failed
                            ? 'min-w-0 flex-1 text-ui-small text-danger-standout'
                            : 'min-w-0 flex-1 text-ui-small text-muted tabular-nums'
                    }
                >
                    {detail}
                </span>
                {action}
            </div>
        </div>
    );
}

/**
 * Opens the change request the import wrote into: its overview once the import is done.
 */
function ReviewChangesButton(props: {
    organizationId: string | null;
    toolCall: { spaceId: string; changeRequestId: string };
    completed: boolean;
}) {
    const { organizationId, toolCall, completed } = props;

    return (
        <Button
            size="xsmall"
            kind={completed ? 'primary' : 'secondary'}
            label={completed ? 'Review changes' : 'Open'}
            trailingIcon={completed ? Icon.ArrowRight : undefined}
            href={{
                current: {
                    type: 'space',
                    organization: organizationId ?? '',
                    space: toolCall.spaceId,
                    page: '',
                    changeRequest: toolCall.changeRequestId,
                    mode: completed ? 'overview' : undefined,
                },
            }}
        />
    );
}

/**
 * What the card says under its title, for each state of the run.
 */
function getDetail(input: {
    status: ImportRunStatus;
    imported: number;
    total: number;
    reason: string | null | undefined;
    /** Whether the import crawls a website, which goes on looking for pages after the last one. */
    website: boolean;
}): string {
    const { status, imported, total, reason, website } = input;
    switch (status) {
        case 'pending':
        case 'in-progress':
            if (total === 0) {
                return 'Finding pages…';
            }
            if (imported >= total) {
                return `${total} of ${total} pages · ${website ? 'checking for more…' : 'finishing up…'}`;
            }
            return `${imported} of ${total} pages`;
        case 'completed':
            return total === 1 ? '1 page imported' : `${total} pages imported`;
        case 'failed':
            return reason ?? 'Something went wrong while importing the content.';
        case 'cancelled':
            return total > 0 ? `Stopped after ${imported} of ${total} pages` : 'Stopped';
    }
}

/**
 * A thin bar of the import's progress: finding the pages, then importing them.
 *
 * A website import only discovers its pages as it goes, so its total is not known up front. The
 * bar follows the pages imported instead, filling more slowly as they add up, so it never has to
 * go back when more pages turn up and never reaches the end before the import does.
 */
function ImportProgressBar(props: { imported: number }) {
    const { imported } = props;
    const percent = Math.round(
        FINDING_PAGES_SHARE + IMPORTING_PAGES_SHARE * (imported / (imported + HALFWAY_PAGES))
    );

    return (
        <div
            role="progressbar"
            aria-label="Import progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            className="relative h-1 w-full overflow-hidden rounded-full bg-neutral-subtle"
        >
            <div
                className="h-full rounded-full bg-primary-solid transition-[width] duration-500"
                style={{ width: `${percent}%` }}
            />
        </div>
    );
}

/**
 * The query reading a spec the agent added, polled until GitBook has processed it.
 */
function getAgentOpenAPISpecQuery(organizationId: string | null, specSlug: string | null) {
    return {
        ...apiQuery(
            'getOpenAPISpecBySlug',
            organizationId && specSlug ? { organizationId, specSlug } : skipToken,
            { nullOnError: true }
        ),
        refetchInterval: (query: { state: { data?: api.OpenAPISpec | null } }) =>
            query.state.data === null ||
            query.state.data?.processingState === api.OpenAPISpecProcessingState.Complete
                ? false
                : OPENAPI_SPEC_POLL_INTERVAL,
    };
}
